import type { RouteLocationNormalized } from "vue-router";
import { getSdk } from "@/composables/useSdk";
import apiClient from "@/services/apiClient";

const REFERRAL_CODE_KEY = "referral_code";
const VISIT_CACHE_KEY = "referral_visit_cache";
const isDebug = import.meta.env.DEV === true;

export function storeReferralCode(code: string): void {
  try {
    localStorage.setItem(REFERRAL_CODE_KEY, code);
  } catch (error) {
    if (isDebug) {
      console.warn("[Referral] Failed to store referral code", error);
    }
  }
}

export function getStoredReferralCode(): string | null {
  try {
    return localStorage.getItem(REFERRAL_CODE_KEY);
  } catch (error) {
    if (isDebug) {
      console.warn("[Referral] Failed to read referral code", error);
    }
    return null;
  }
}

export function clearStoredReferralCode(): void {
  try {
    localStorage.removeItem(REFERRAL_CODE_KEY);
  } catch (error) {
    if (isDebug) {
      console.warn("[Referral] Failed to clear referral code", error);
    }
  }
}

/** Persist ref in httpOnly cookie for OAuth signup attribution (backend ReferralCodeResolver). */
export async function syncReferralToAuthCookie(
  referralCode?: string | null,
): Promise<void> {
  const ref = referralCode?.trim();
  if (!ref) return;

  try {
    await apiClient.post(
      "/v1/auth/referral",
      { ref },
      { skipAuth: true, credentials: "include" },
    );
  } catch (error) {
    if (isDebug) {
      console.warn("[Referral] Failed to sync ref cookie", error);
    }
  }
}

function markVisitRecorded(cacheKey: string): void {
  try {
    const cache = JSON.parse(sessionStorage.getItem(VISIT_CACHE_KEY) || "{}") as Record<string, boolean>;
    cache[cacheKey] = true;
    sessionStorage.setItem(VISIT_CACHE_KEY, JSON.stringify(cache));
  } catch (error) {
    if (isDebug) {
      console.warn("[Referral] Failed to persist visit cache", error);
    }
  }
}

function hasVisitRecorded(cacheKey: string): boolean {
  try {
    const cache = JSON.parse(sessionStorage.getItem(VISIT_CACHE_KEY) || "{}") as Record<string, boolean>;
    return Boolean(cache[cacheKey]);
  } catch {
    return false;
  }
}

export function buildDestinationFromRoute(
  to: RouteLocationNormalized,
): { destinationType?: string; destinationId?: string } {
  switch (to.name) {
    case "profile":
      return { destinationType: "profile", destinationId: String(to.params.username || "") };
    case "playlist-detail":
      return { destinationType: "playlist", destinationId: String(to.params.id || "") };
    case "song-detail":
      return { destinationType: "song", destinationId: String(to.params.ulid || "") };
    default: {
      const ulid = (to.params as Record<string, unknown>).ulid;
      if (ulid) {
        return { destinationType: "content", destinationId: String(ulid) };
      }
      return {};
    }
  }
}

export async function recordReferralVisit(params: {
  referralCode: string;
  destinationType?: string;
  destinationId?: string;
  landingPath?: string;
}): Promise<void> {
  const cacheKey = `${params.referralCode}:${params.landingPath ?? ""}`;
  if (hasVisitRecorded(cacheKey)) {
    return;
  }

  try {
    const sdk = getSdk();
    await sdk.recordReferralVisit({
      referralCode: params.referralCode,
      destinationType: params.destinationType,
      destinationId: params.destinationId,
      landingPath: params.landingPath,
    });
    markVisitRecorded(cacheKey);
  } catch (error) {
    if (isDebug) {
      console.error("[Referral] Failed to record referral visit", error);
    }
  }
}
